// Terrengskygge fra DEM: hvilke celler er solbelyste og hvilke ligger i skygge
// av annet terreng, ved en gitt solretning. Brukes til skjult forflytning,
// observasjonsposter (motlys/skyggeside) og til å vurdere hvor lenge en dal
// ligger i skygge.
//
// ── ALGORITME ───────────────────────────────────────────────────────────────
// Klassisk «ray casting» / horisontvinkel-metode: fra hver celle marsjeres det
// langs den horisontale retningen MOT sola, og den største terrengvinkelen
// («horisontvinkelen») langs strålen sammenlignes med solas elevasjon. Er
// horisontvinkelen større, blokkerer terrenget sollyset → cellen er i skygge.
// Dette er samme prinsipp som `lineOfSight`/`viewshed` i @skade/contracts, men
// mot et punkt i det uendelige (sola) framfor mot et annet terrengpunkt.
//
// FORUTSETNINGER OG GRENSER — les dem:
//   * Kun BINÆR geometrisk skygge (kjerneskygge). Ingen halvskygge fra solas
//     halve diameter (0.27°), ingen diffust himmellys, ingen refleksjon fra
//     omkringliggende snø/vann. En celle merket «skygge» er ikke svart; på klar
//     dag får den fortsatt 10-20 % av innstrålingen som diffust lys.
//   * JORDKRUMMING er FORSØMT som standard. Fallhøyden er d²/2R: 0.08 m på
//     1 km, 7.8 m på 10 km. Uttrykt som vinkel er det 0.0045°/km — mot typiske
//     terrengvinkler på flere grader er det uten betydning på skygge-skala
//     (< 10 km). `earthCurvature: true` slår det på for lange, flate siktlinjer
//     over fjord; da regnes også atmosfærisk refraksjon inn med standard
//     k = 0.13 (effektiv radius 7/6·R).
//   * VEGETASJON OG BYGG er ikke i en DEM (bare i en DSM). Skog kaster reell
//     skygge som denne modellen ikke ser. Bruk `objectShadow` for enkeltobjekt.
//   * Cellene behandles metrisk via `gridCellMetrics`. `ElevationGrid` er
//     ekvirektangulært, så en celle er IKKE kvadratisk: på 60°N er øst-vest-
//     avstanden halvparten av nord-sør for samme gradsteg. Default 'geodetic'
//     regner dette ut riktig. 'square' bruker `cellSizeM` i begge retninger,
//     som er samme forenkling `slopeAspect` i @skade/contracts gjør — velg det
//     hvis du vil være konsistent med helningstallene derfra.
//   * Kompleksitet O(rader·kolonner·maxCells). Sett `maxCells` for store DEM-er.
import { elevationAt, elevationAtCell, haversineM, slopeAspect, } from '@skade/contracts';
import { normalizeDeg } from './sun.js';
const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
/** Jordas middelradius (m) — samme verdi som geo.ts bruker. */
const EARTH_R = 6_371_000;
/**
 * Bakkeavstand per celle. 'geodetic' regner ut faktisk avstand for lat/lonStep
 * ved rutenettets midtbredde (gjenbruker `haversineM`); 'square' bruker
 * `cellSizeM` i begge retninger.
 */
export function gridCellMetrics(g, mode = 'geodetic') {
    if (mode === 'square')
        return { dxM: g.cellSizeM, dyM: g.cellSizeM };
    const midLat = g.north - ((g.rows - 1) / 2) * g.latStep;
    return {
        dxM: haversineM({ lat: midLat, lon: g.west }, { lat: midLat, lon: g.west + g.lonStep }),
        dyM: haversineM({ lat: midLat, lon: g.west }, { lat: midLat - g.latStep, lon: g.west }),
    };
}
/**
 * Bilineær høyde ved brøk-celleindeks (rad, kolonne). Regner om til lat/lon og
 * gjenbruker `elevationAt` fra @skade/contracts (samme interpolasjon og samme
 * hull-håndtering).
 */
function elevationAtIndex(g, row, col) {
    return elevationAt(g, g.north - row * g.latStep, g.west + col * g.lonStep);
}
/**
 * Terrengets horisontvinkel sett fra celle [row,col] i retning `bearingDeg`
 * (grader fra nord, med klokka): den største vinkelen over horisontalplanet som
 * terrenget langs strålen dekker. Negativ verdi = terrenget faller bort i den
 * retningen. Returnerer null hvis startcellen er et hull.
 */
export function horizonAngleDeg(g, row, col, bearingDeg, opts = {}) {
    const z0 = elevationAtCell(g, row, col);
    if (z0 === null)
        return null;
    const { dxM, dyM } = gridCellMetrics(g, opts.cellMetrics);
    const maxCells = opts.maxCells ?? Math.max(g.rows, g.cols);
    const b = normalizeDeg(bearingDeg) * DEG;
    // Retningsvektor i celleenheter: kolonne øker mot øst, rad øker mot SØR.
    const dCol = Math.sin(b);
    const dRow = -Math.cos(b);
    // Metrisk lengde av ett celle-steg langs denne retningen.
    const stepM = Math.hypot(dCol * dxM, dRow * dyM);
    if (stepM === 0)
        return null;
    // Effektiv radius med refraksjon: R_eff = R/(1−k), k = 0.13 → ~7/6·R.
    const rEff = EARTH_R / (1 - 0.13);
    let best = -90;
    // EPS tåler flyttalls-slark: Math.cos(90°) er 6.1e-17, ikke 0, så en stråle
    // rett østover ville ellers «forlate» rutenettet umiddelbart fra rad 0.
    const EPS = 1e-9;
    for (let k = 1; k <= maxCells; k++) {
        let r = row + k * dRow;
        let c = col + k * dCol;
        if (r < -EPS || c < -EPS || r > g.rows - 1 + EPS || c > g.cols - 1 + EPS)
            break;
        r = Math.min(Math.max(r, 0), g.rows - 1);
        c = Math.min(Math.max(c, 0), g.cols - 1);
        const z = elevationAtIndex(g, r, c);
        if (z === null)
            continue; // hull: ingen informasjon, hopp over
        const distM = k * stepM;
        let dz = z - z0;
        if (opts.earthCurvature)
            dz -= (distM * distM) / (2 * rEff);
        const angle = Math.atan2(dz, distM) * RAD;
        if (angle > best)
            best = angle;
    }
    return best;
}
// ── Skyggemaske ─────────────────────────────────────────────────────────────
/** Cellen er solbelyst. */
export const SUNLIT = 0;
/** Cellen ligger i terrengskygge (eller sola er under horisonten). */
export const SHADED = 1;
/** Ingen data (hull i DEM-en). */
export const NODATA = 2;
/** Er celle [row,col] i skygge? (NODATA regnes ikke som skygge.) */
export function isShaded(mask, row, col) {
    if (row < 0 || col < 0 || row >= mask.rows || col >= mask.cols)
        return false;
    return mask.state[row * mask.cols + col] === SHADED;
}
/** Tilstanden i celle [row,col]: SUNLIT, SHADED eller NODATA. */
export function shadowStateAt(mask, row, col) {
    if (row < 0 || col < 0 || row >= mask.rows || col >= mask.cols)
        return NODATA;
    return mask.state[row * mask.cols + col];
}
/**
 * Terrengskygge for hele rutenettet ved gitt solretning.
 *
 * Er solelevasjonen ≤ 0 er hele området i skygge (natt) — det er den korrekte
 * svaret, ikke et spesialtilfelle som må håndteres av kalleren.
 */
export function terrainShadow(g, opts) {
    const state = new Uint8Array(g.rows * g.cols);
    const sunBelowHorizon = opts.sunElevationDeg <= 0;
    let withData = 0;
    let shaded = 0;
    for (let row = 0; row < g.rows; row++) {
        for (let col = 0; col < g.cols; col++) {
            const i = row * g.cols + col;
            if (elevationAtCell(g, row, col) === null) {
                state[i] = NODATA;
                continue;
            }
            withData++;
            if (sunBelowHorizon) {
                state[i] = SHADED;
                shaded++;
                continue;
            }
            // Se MOT sola: horisontvinkelen i solas asimut-retning.
            const h = horizonAngleDeg(g, row, col, opts.sunAzimuthDeg, opts);
            if (h !== null && h > opts.sunElevationDeg) {
                state[i] = SHADED;
                shaded++;
            }
            else {
                state[i] = SUNLIT;
            }
        }
    }
    return {
        rows: g.rows,
        cols: g.cols,
        state,
        sunBelowHorizon,
        shadedFraction: withData === 0 ? 0 : shaded / withData,
        sunAzimuthDeg: opts.sunAzimuthDeg,
        sunElevationDeg: opts.sunElevationDeg,
    };
}
/** Skygge for én enkelt celle (billigere enn hele masken). */
export function cellShadowState(g, row, col, opts) {
    if (elevationAtCell(g, row, col) === null)
        return NODATA;
    if (opts.sunElevationDeg <= 0)
        return SHADED;
    const h = horizonAngleDeg(g, row, col, opts.sunAzimuthDeg, opts);
    return h !== null && h > opts.sunElevationDeg ? SHADED : SUNLIT;
}
/**
 * Skyggelengde og -retning for et objekt av høyde `heightM` på flat, horisontal
 * bakke: L = h / tan(solelevasjon), skyggen faller motsatt solas asimut.
 *
 * Merk: på skrå bakke blir skyggen lengre nedover og kortere oppover enn dette;
 * bruk `terrainShadow` når terrenget betyr noe. Ved solelevasjon under ~1° blir
 * L svært stor og samtidig fysisk meningsløs (uskarp halvskygge, refraksjon).
 */
export function objectShadow(heightM, sunElevationDeg, sunAzimuthDeg) {
    const bearingDeg = normalizeDeg(sunAzimuthDeg + 180);
    if (sunElevationDeg <= 0)
        return { lengthM: null, bearingDeg, state: 'ingen-sol' };
    return {
        lengthM: heightM / Math.tan(sunElevationDeg * DEG),
        bearingDeg,
        state: 'skygge',
    };
}
/**
 * Relativ solinnstråling per celle, 0-1: cosinus av innfallsvinkelen på den
 * skrå celleflata, satt til 0 der cellen er i skygge.
 *
 *   cos i = cos(helning)·sin(h☉) + sin(helning)·cos(h☉)·cos(A☉ − eksposisjon)
 *
 * Gjenbruker `slopeAspect` (Horns metode) fra @skade/contracts. Verdien er
 * REN GEOMETRI: 1 betyr «sola står normalt på flata», ikke W/m². Atmosfærisk
 * dempning, diffust himmellys, albedo og skydekke er ikke med, så tallet er
 * best egnet til å sammenligne celler mot hverandre (sol- kontra skyggeside,
 * hvor snøen ligger lengst, hvor et kjøretøy blir varmt for IR-signatur).
 * Celler uten data får 0.
 */
export function solarExposure(g, opts) {
    const out = new Float32Array(g.rows * g.cols);
    if (opts.sunElevationDeg <= 0)
        return out; // natt: ingen innstråling
    const mask = opts.mask ?? terrainShadow(g, opts);
    const sinH = Math.sin(opts.sunElevationDeg * DEG);
    const cosH = Math.cos(opts.sunElevationDeg * DEG);
    for (let row = 0; row < g.rows; row++) {
        for (let col = 0; col < g.cols; col++) {
            const i = row * g.cols + col;
            if (shadowStateAt(mask, row, col) !== SUNLIT)
                continue;
            const sa = slopeAspect(g, row, col);
            let cosIncidence;
            if (sa === null) {
                cosIncidence = sinH; // kanten/hull i nabolaget: regn flata som horisontal
            }
            else {
                const slope = sa.slopeDeg * DEG;
                cosIncidence =
                    Math.cos(slope) * sinH +
                        Math.sin(slope) * cosH * Math.cos((opts.sunAzimuthDeg - sa.aspectDeg) * DEG);
            }
            out[i] = Math.max(0, cosIncidence);
        }
    }
    return out;
}
//# sourceMappingURL=shadow.js.map